import * as React from "react"
import { Button } from "./button"
import { Input } from "./input"
import { Label } from "./label"
import { Switch } from "./switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip"
import { Facebook, Instagram, Linkedin, Send, Twitter, Sun, Moon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import { useThemeMode } from "../../contexts/ThemeContext"

function FooterSection() {
  const navigate = useNavigate()
  const { mode, toggleTheme } = useThemeMode()
  const [email, setEmail] = React.useState("")

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      toast.success("Thank you for subscribing!")
      setEmail("")
    }
  }

  const isDark = mode === 'dark';

  return (
    <footer 
      className="relative transition-colors duration-300"
      style={{
        backgroundColor: isDark ? '#0f0f0f' : '#ffffff',
        color: isDark ? '#ffffff' : '#1F2937',
        borderTop: `1px solid ${isDark ? 'rgba(55, 65, 81, 1)' : 'rgba(229, 231, 235, 1)'}`,
      }}
    >
      <div className="container mx-auto px-4 py-12 md:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <h2 className="mb-4 text-3xl font-bold tracking-tight">Stay Connected</h2>
            <p className="mb-6" style={{ color: isDark ? '#A1A1AA' : '#6B7280' }}>
              Join our newsletter for the latest updates and exclusive offers.
            </p>
            <form className="relative" onSubmit={handleNewsletterSubmit}>
              <Input
                type="email"
                placeholder="Enter your email"
                className="pr-12"
                style={{
                  backgroundColor: isDark ? 'rgba(139, 92, 246, 0.05)' : '#FFFFFF',
                  borderColor: isDark ? 'rgba(139, 92, 246, 0.1)' : '#e5e7eb',
                  color: isDark ? '#ffffff' : '#1F2937',
                }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Button
                type="submit"
                size="icon"
                className="absolute right-1 top-1 h-8 w-8 rounded-full transition-transform hover:scale-105"
                style={{
                  backgroundColor: isDark ? '#8B5CF6' : '#7C3AED',
                  color: '#ffffff',
                }}
              >
                <Send className="h-4 w-4" />
                <span className="sr-only">Subscribe</span>
              </Button>
            </form>
            <div 
              className="absolute -right-4 top-0 h-24 w-24 rounded-full blur-2xl" 
              style={{ backgroundColor: isDark ? 'rgba(139, 92, 246, 0.1)' : 'rgba(124, 58, 237, 0.1)' }}
            />
          </div>
          <div>
            <h3 className="mb-4 text-lg font-semibold">Quick Links</h3>
            <nav className="space-y-2 text-sm">
              {[
                { path: "/", label: "Home" },
                { path: "/dashboard", label: "Dashboard" },
                { path: "/talent-tree", label: "Health Tree" },
                { path: "/weekly-lockin", label: "Weekly Commitment" }
              ].map(({ path, label }) => (
                <button 
                  key={path}
                  onClick={() => navigate(path)} 
                  className="block transition-colors text-left hover:opacity-80"
                  style={{ 
                    color: isDark ? '#ffffff' : '#1F2937',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = isDark ? '#8B5CF6' : '#7C3AED'}
                  onMouseLeave={(e) => e.currentTarget.style.color = isDark ? '#ffffff' : '#1F2937'}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-semibold">Contact Us</h3>
            <div className="space-y-2 text-sm">
              <button 
                onClick={() => navigate('/contact')} 
                className="block transition-colors text-left hover:opacity-80 mb-2"
                style={{ 
                  color: isDark ? '#8B5CF6' : '#7C3AED',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Contact us
              </button>
              <address className="not-italic" style={{ color: isDark ? '#A1A1AA' : '#6B7280' }}>
                <p>Available: Mon-Fri, 9AM-5PM EST</p>
              </address>
            </div>
          </div>
          <div className="relative">
            <h3 className="mb-4 text-lg font-semibold">Follow Us</h3>
            <div className="mb-6 flex space-x-4">
              {[
                { icon: Facebook, label: "Facebook", tooltip: "Follow us on Facebook" },
                { icon: Twitter, label: "Twitter", tooltip: "Follow us on Twitter" },
                { icon: Instagram, label: "Instagram", tooltip: "Follow us on Instagram" },
                { icon: Linkedin, label: "LinkedIn", tooltip: "Connect with us on LinkedIn" }
              ].map(({ icon: Icon, label, tooltip }) => (
                <TooltipProvider key={label}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="rounded-full"
                        style={{
                          borderColor: isDark ? 'rgba(139, 92, 246, 0.1)' : '#e5e7eb',
                          color: isDark ? '#ffffff' : '#1F2937',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="sr-only">{label}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
            <div className="flex items-center space-x-2 mt-6">
              <Sun className="h-4 w-4" style={{ color: isDark ? '#A1A1AA' : '#6B7280' }} />
              <Switch
                id="theme-mode"
                checked={mode === 'dark'}
                onCheckedChange={toggleTheme}
              />
              <Moon className="h-4 w-4" style={{ color: isDark ? '#A1A1AA' : '#6B7280' }} />
              <Label htmlFor="theme-mode" className="sr-only">
                Toggle theme mode
              </Label>
            </div>
          </div>
        </div>
        <div 
          className="mt-12 flex flex-col items-center justify-between gap-4 pt-8 text-center md:flex-row"
          style={{ borderTop: `1px solid ${isDark ? 'rgba(139, 92, 246, 0.1)' : '#e5e7eb'}` }}
        >
          <p className="text-sm" style={{ color: isDark ? '#A1A1AA' : '#6B7280' }}>
            © {new Date().getFullYear()} Health-Tree. All rights reserved.
          </p>
          <nav className="flex gap-4 text-sm">
            {[
              { path: "/privacy-policy", label: "Privacy Policy" },
              { path: "/terms-of-service", label: "Terms of Service" }
            ].map(({ path, label }) => (
              <button 
                key={path}
                onClick={() => navigate(path)} 
                className="transition-colors hover:opacity-80"
                style={{ color: isDark ? '#A1A1AA' : '#6B7280' }}
                onMouseEnter={(e) => e.currentTarget.style.color = isDark ? '#8B5CF6' : '#7C3AED'}
                onMouseLeave={(e) => e.currentTarget.style.color = isDark ? '#A1A1AA' : '#6B7280'}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  )
}

export { FooterSection }